import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  CircularProgress,
  createFilterOptions,
  FormControl,
  FormHelperText,
  TextField as MuiTextField,
  Tooltip,
  IconButton,
  useTheme,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {
  ListFooter,
  OptionCode,
  OptionDescription,
  OptionRow,
  SearchableSelectFieldRoot,
  StyledAutocompletePaper,
  autocompleteFieldSx,
} from "./SearchableSelectField.styles";

const DEFAULT_DEBOUNCE_MS = 300;

const defaultClientFilter = createFilterOptions({
  stringify: (option) =>
    `${option?.id || ""} ${option?.label || ""} ${option?.description || ""}`,
});

const ListboxWithFooter = React.forwardRef(function ListboxWithFooter(
  props,
  ref,
) {
  const { children, loadingMore, onScroll, ...other } = props;
  return (
    <ul
      {...other}
      ref={ref}
      onScroll={onScroll}
      style={{
        ...other.style,
        maxHeight: 280,
        overflow: "auto",
      }}
    >
      {children}
      {loadingMore ? (
        <ListFooter component="div" role="presentation">
          <CircularProgress size={16} />
          Loading more…
        </ListFooter>
      ) : null}
    </ul>
  );
});

/**
 * Reusable async-friendly MUI Autocomplete for Formik forms.
 * Expects options as `{ id, label, description? }`; `value` is the selected `id`.
 *
 * @param {boolean} [clientSideFilter] - When true, filter options locally by code/label
 *   (countries). When false (default), filtering is delegated to `onSearch` (MCC).
 */
const SearchableSelectField = ({
  name,
  label,
  value = "",
  onChange,
  onBlur,
  options = [],
  resolvedOption = null,
  onSearch,
  onLoadMore,
  onOpen,
  loading = false,
  loadingMore = false,
  hasMore = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  required = false,
  error = "",
  touched = false,
  disabled = false,
  helperText,
  placeholder = "Search by code or description…",
  noOptionsText = "No matches found",
  fullWidth = true,
  clientSideFilter = false,
  sx = {},
}) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");
  const debounceRef = useRef(null);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const fromList = options.find((opt) => opt.id === value);
    if (fromList) return fromList;
    if (resolvedOption?.id === value) return resolvedOption;
    return { id: value, label: value };
  }, [value, options, resolvedOption]);

  useEffect(() => {
    if (selectedOption?.label) {
      setInputValue(selectedOption.label);
    } else if (!value) {
      setInputValue("");
    }
  }, [selectedOption, value]);

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  const scheduleSearch = useCallback(
    (term) => {
      if (!onSearch) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearch(term);
      }, debounceMs);
    },
    [onSearch, debounceMs],
  );

  const handleInputChange = (_, newInputValue, reason) => {
    if (reason === "reset") return;
    setInputValue(newInputValue);
    if (reason === "input" || reason === "clear") {
      scheduleSearch(newInputValue || "");
    }
  };

  const handleChange = (_, newOption) => {
    onChange?.({
      target: {
        name,
        value: newOption?.id ?? "",
      },
    });
    if (newOption?.label) {
      setInputValue(newOption.label);
    }
  };

  const handleListboxScroll = (event) => {
    if (!onLoadMore || !hasMore || loading || loadingMore) return;
    const node = event.currentTarget;
    const nearBottom =
      node.scrollTop + node.clientHeight >= node.scrollHeight - 40;
    if (nearBottom) {
      onLoadMore();
    }
  };

  const hasError = touched && Boolean(error);
  const errorMessage = hasError ? error : "";

  const renderLabel = () => (
    <>
      {label}
      {helperText && (
        <Tooltip title={helperText} arrow>
          <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
            <InfoIcon color="primary" fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <SearchableSelectFieldRoot sx={sx}>
      <FormControl fullWidth={fullWidth} required={required} error={hasError}>
        <Autocomplete
          id={name}
          disabled={disabled}
          loading={loading}
          sx={autocompleteFieldSx(theme)}
          value={selectedOption}
          inputValue={inputValue}
          options={options}
          filterOptions={
            clientSideFilter ? defaultClientFilter : (opts) => opts
          }
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option?.label || ""
          }
          isOptionEqualToValue={(option, selected) =>
            option?.id === selected?.id
          }
          onOpen={onOpen}
          onChange={handleChange}
          onInputChange={handleInputChange}
          onBlur={() => onBlur?.({ target: { name } })}
          noOptionsText={loading ? "Loading…" : noOptionsText}
          PaperComponent={StyledAutocompletePaper}
          ListboxComponent={ListboxWithFooter}
          ListboxProps={{
            loadingMore,
            onScroll: handleListboxScroll,
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <OptionRow>
                <OptionCode>{option.id}</OptionCode>
                <OptionDescription title={option.description || option.label}>
                  {option.description ||
                    option.label?.replace(/^[^-]+-\s*/, "") ||
                    option.label}
                </OptionDescription>
              </OptionRow>
            </li>
          )}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              name={name}
              label={renderLabel()}
              placeholder={placeholder}
              required={required}
              error={hasError}
              InputLabelProps={{
                ...params.InputLabelProps,
                sx: {
                  "& .MuiFormLabel-asterisk": {
                    color: "red",
                    fontSize: "1.2rem",
                  },
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {hasError && <FormHelperText error>{errorMessage}</FormHelperText>}
      </FormControl>
    </SearchableSelectFieldRoot>
  );
};

export default SearchableSelectField;
